import { z } from 'zod';

import { SelectedPlayers } from '@/components/constants/types';
import { Match } from '@/entities';

/** Minimal player shape the doubles-rating calculation needs — satisfied by any roster source. */
export interface DoublesRatingPlayer {
	userUuid: string;
	ratingDoubles: number | null;
}

export interface CalculateDoublesRating {
	match: Match | undefined;
	homePlayers: DoublesRatingPlayer[];
	awayPlayers: DoublesRatingPlayer[];
	currentSelectedPlayers?: SelectedPlayers;
	isEnabled: boolean;
}

export interface ICheckOptions {
	id: string;
	label: string;
	value: boolean | string;
}

export type Options = {
	value: string;
	label: string;
	valueGameday?: string | null;
};

export const checkGenderOptions: ICheckOptions[] = [
	{
		id: '14',
		label: 'Male',
		value: 'M'
	},
	{
		id: '15',
		label: 'Female',
		value: 'F'
	}
];

export const createUserSchema = z.object({
	email: z.string().trim().email('Please provide a valid email.').min(1, 'Email is required.').max(50, 'Email must not exceed 50 characters.'),
	firstName: z.string().trim().min(1, 'First name must contains at least 1 character.').max(50, 'First name must not exceed 50 characters.'),
	lastName: z.string().trim().min(1, 'Last name must contains at least 1 character.').max(50, 'Last name must not exceed 50 characters.'),
	phone: z.string().trim().min(8, 'Phone number must contains minimum 8 numbers.'),
	phoneCountryId: z.coerce.number().min(1, 'You have to select a phone country ID.'),
	countryId: z.number().min(1, 'Country is required.'),
	stateId: z.number().min(1, 'State is required.'),
	zip: z.string().trim().min(1, 'Zip/Postal is required.'),
	dob: z.string().trim().min(1, 'Date of birth is required.'),
	gender: z.string(),
	moduleFor: z.number().optional(),
	moduleId: z.number().optional()
});

export type UserCreateFormType = z.infer<typeof createUserSchema>;

export enum UserCreateOrigin {
	UNDEFINED_USER_CREATE_ORIGIN = 0,
	ORGANIZATIONS_USER_CREATE_ORIGIN = 1,
	THIRD_PARTY_USER_CREATE_ORIGIN = 2,
	TEAM_LEAGUES_USER_CREATE_ORIGIN = 3,
	TEAM_LEAGUES_TEAM_USER_CREATE_ORIGIN = 4
}

export type Unit = 'second' | 'minute' | 'hour' | 'day' | 'month' | 'year';

export type TieBreakerPair = {
	player1: string;
	player2: string;
};

export type Pod = {
	uuid: string;
	name: string;
};

export type PodsResponse = {
	pods: Pod[];
};

export enum TeamLeagueTypes {
	Managing = 'Manage',
	RegisteredFor = 'RegisteredFor',
	Favorites = 'Favorites'
}

export const teamLeagueTypeMap: Record<string, TeamLeagueTypes> = {
	registeredfor: TeamLeagueTypes.RegisteredFor,
	manage: TeamLeagueTypes.Managing,
	favorites: TeamLeagueTypes.Favorites
};

export interface TeamLeagueLabels {
	tieBreakerMatchTitle: string;
	tieBreakerMatchPluralTitle: string;
	districtSingularTitle: string;
	districtPluralTitle: string;
	matchupSingularTitle: string;
	matchupPluralTitle: string;
	tieBreakerMatchAbbreviation: string;
}

export enum SeasonRegistrationSettingsPlayerEligibility {
	PlayerEligibility_UNDEFINED = 'PlayerEligibility_UNDEFINED',
	PlayerEligibility_BOTH = 'PlayerEligibility_BOTH',
	PlayerEligibility_REGISTRATION_FEE_PAID = 'PlayerEligibility_REGISTRATION_FEE_PAID',
	PlayerEligibility_WAIVERS_SIGNED = 'PlayerEligibility_WAIVERS_SIGNED'
}

export interface TimezoneLegacy {
	id: number; // Wrong, API returns string
	utcOffset: number; // v1 API returns string, v2 API returns number
	name: string;
	abbreviation: string;
	fullName: string;
	hasDst: boolean;
	ianaTzIdentifier: string;
	daylightChange: Date | undefined; // Wrong, API returns string | null
	nonDaylightChange: Date | undefined; // Wrong, API returns string | null
	daylightSavingAbbreviation: string;
	daylightSavingUtcOffset: number; // v1 API returns string, v2 API returns number
}

export enum MyTeamLeaguesTypes {
	Managing = 'manager',
	RegisteredFor = 'registeredFor',
	Favorited = 'favorites'
}
